import { resolveDesignProfile, type DesignProfile, type ElectricalDesignPolicy } from "./design-profiles";
import { effectiveChargeCurrent } from "./charge-current";
import { outputTerminalCurrent } from "./output-current-limits";
import { computeLiveCableCurrents } from "./live-cable-flow";
import { computePvCurrentAnalysis } from "./pv-currents";
import { acCableInfo } from "./ac";
import { isFuseBlockLike, isFuseHolderVariant } from "./auto-fuse";
import { resolveCablePolarity } from "./cable-color";
import { resolveCableNominalVoltage } from "./cable-voltage";
import { getDefinition, type DefLookup } from "./catalog";
import { componentLoadCurrents, dcDcInputCurrent } from "./component-current";
import { dcChargingPortGroups, dcPortTerminalGroups, isInverterCharger, isPvTerminalId, pvInputTerminalGroups } from "./component-ports";
import { isJunction } from "./node-kind";
import { isSenseCable, isSenseTerminal } from "./sense-terminal";
import { conflictingFuseRatings } from "./protection-devices";
import { cableFuseFamilyExcludesRating } from "./fuse-aic";
import { fuseExceedsTerminalLimit, seriesFuseTerminals } from "./terminal-fuse-limit";
import { cableSizingDropWeight, cableVoltageDropWeight } from "./load-circuits";
import type { FridgeCablePolicy } from "./fridge-installation-policy";
import { installationBreakerRating, installationWireMinimum } from "./installation-limits";
import {
  applyStateToAmp,
  getActiveIntraLinkTargets,
  getEffectiveStateOption,
  type SimulateStateOption,
} from "./simulate/states";
import { terminalPolarity } from "./terminal-snap";
import type {
  Cable,
  ComponentDefinition,
  FuseType,
  SystemNode,
  Terminal,
} from "./types";
import {
  recommendCrankingFuse,
  recommendFuse,
  selectWire,
  VDROP_PCT_CRITICAL,
  voltageDrop,
  wireByMm2,
  type WireSpec,
} from "./wire-tables";

/** Simulate-mode context. A leaf whose {@link energizedKey} is outside
 *  `energizedTerminals` resolves to 0 A; omit the set for the static spec. */
export type SimulateContext = {
  componentStates: Record<string, string>;
  /** Per-load on/off. `active: false` zeroes the leaf and stops the role walk. */
  loadStates?: Record<string, { active: boolean }>;
  energizedTerminals?: ReadonlySet<string>;
};

export const energizedKey = (nodeId: string, terminalId: string): string =>
  `${nodeId}:${terminalId}`;

/** The cable's own override only — {@link getEffectiveCableFuse} adds inherited fuses. */
export function getCableFuse(cable: Cable): number | undefined {
  return cable.fuseAmpOverride ?? undefined;
}

/** Fuse protecting a cable: the cable's own override wins, else an inherited
 *  battery-post or fuse-holder rating. Rating and type resolve independently. */
export type EffectiveCableFuse = {
  ratingA?: number;
  type?: FuseType;
  /** An inherited *fuse-holder* rating also reports `"battery"`. */
  source: { rating: "cable" | "battery" | "none"; type: "cable" | "battery" | "none" };
  /** The inline holder's own rating when this cable lands on either of its
   *  posts. Unlike a post MRBF (sized for the bank, reported by
   *  `inline-fuse-needed`), a holder rating is the OCPD of *this* conductor
   *  and so an ampacity floor for it. */
  holderRatingA?: number;
  /** Cable and attached device disagree; their rating/family cannot be merged. */
  identityConflict?: boolean;
};

export function getEffectiveCableFuse(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  defLookup: DefLookup,
): EffectiveCableFuse {
  const ownRating = cable.fuseAmpOverride ?? undefined;
  const ownType = cable.fuseTypeOverride;
  let battRating: number | undefined;
  let battType: FuseType | undefined;
  let holderRating: number | undefined;
  let holderType: FuseType | undefined;
  for (const end of [
    { nodeId: cable.fromNodeId, termId: cable.fromTerminalId },
    { nodeId: cable.toNodeId, termId: cable.toTerminalId },
  ]) {
    const node = nodesById[end.nodeId];
    if (!node || node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    if (node.batteryFuse && def.category === "battery") {
      const term = def.terminals.find((t) => t.id === end.termId);
      if (term?.polarity === "positive") {
        battRating = node.batteryFuse.ratingA;
        battType = node.batteryFuse.type;
      }
    }
    // An inline holder is one element in series with both of its cables, so
    // the rating protects (and must be hosted by) the `in` side as much as the
    // `out` side — `resolveCableFuseType` already treats it symmetrically.
    // Applying it to the output only left a 6 mm² feed on the `in` post of a
    // 150 A MEGA holder unprotected and unflagged (vailoa-1). Multi-slot
    // blocks keep their per-slot ratings on the output cables.
    if (isFuseHolderVariant(def) && node.fuseAmp != null && node.fuseAmp > 0) {
      const term = def.terminals.find((t) => t.id === end.termId);
      if (term?.direction === "output" || term?.direction === "input") {
        holderRating = node.fuseAmp;
        if (def.fuseSlotType) holderType = def.fuseSlotType;
      }
    }
  }
  // Same rating as the post's own fuse is one OCPD, and routes the diagnostic
  // to `inline-fuse-needed` rather than a fuse-vs-wire warning.
  const ownRatingMirrorsBattery =
    ownRating != null && battRating != null && ownRating === battRating;
  const ownTypeMirrorsBattery =
    ownType != null && battType != null && ownType === battType;
  const identityConflict = conflictingFuseRatings(ownRating, [holderRating, battRating]);
  const inheritedType = identityConflict ? undefined : holderType ?? battType;
  const ratingSource: "cable" | "battery" | "none" =
    ownRating != null && !ownRatingMirrorsBattery
      ? "cable"
      : (holderRating ?? battRating) != null
        ? "battery"
        : "none";
  const typeSource: "cable" | "battery" | "none" =
    ownType != null && (!ownTypeMirrorsBattery || identityConflict)
      ? "cable"
      : inheritedType != null
        ? "battery"
        : "none";
  return {
    ratingA: ownRating ?? holderRating ?? battRating,
    type: ownType ?? inheritedType,
    source: { rating: ratingSource, type: typeSource },
    holderRatingA: holderRating,
    ...(identityConflict ? { identityConflict: true } : {}),
  };
}

/** Battery post on one end, distribution hardware (switch, bus, junction,
 *  shunt, fuse holder / block) on the other: the run every amp of the bank
 *  crosses, whose only OCPD is the post fuse. */
function isBankTrunk(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  defLookup: DefLookup,
): boolean {
  const ends = [nodesById[cable.fromNodeId], nodesById[cable.toNodeId]];
  const defs = ends.map((n) =>
    n?.kind === "component" ? defLookup(n.definitionId, n.customDefinition) : undefined,
  );
  const battIdx = defs.findIndex((d) => d?.category === "battery");
  if (battIdx < 0) return false;
  const other = defs[1 - battIdx];
  const otherNode = ends[1 - battIdx];
  if (!other || !otherNode) return false;
  if (other.category === "battery") return false;
  return (
    other.category === "distribution" ||
    isJunction(otherNode, other) ||
    isShuntPassthrough(other)
  );
}

/** ABYC E-11 Table V: 105 °C insulation at 50 °C ambient carries ~82 % of its
 *  30 °C ampacity. */
export const ENGINE_BAY_AMPACITY_DERATING = 0.82;

export function cableAmbientDerating(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  engineBayDerating = ENGINE_BAY_AMPACITY_DERATING,
): number {
  // Explicit total installation factor, including heat, survives a change in
  // diagram layout. Invalid imported factors fail conservatively, never >1.
  if (cable.ampacityDerating != null) {
    return Number.isFinite(cable.ampacityDerating) && cable.ampacityDerating > 0 && cable.ampacityDerating <= 1
      ? cable.ampacityDerating : 0.01;
  }
  const fromNode = nodesById[cable.fromNodeId];
  const toNode = nodesById[cable.toNodeId];
  if (!fromNode || !toNode) return 1;
  if (fromNode.kind !== "component" || toNode.kind !== "component") return 1;
  const fromParent = fromNode.parentId ? nodesById[fromNode.parentId] : undefined;
  const toParent = toNode.parentId ? nodesById[toNode.parentId] : undefined;
  if (fromParent?.kind !== "zone" || toParent?.kind !== "zone") return 1;
  if (!fromParent.isEngineBay || !toParent.isEngineBay) return 1;
  return engineBayDerating;
}

/** One end of a cable. An absent field means "doesn't constrain" — not zero. */
type EndpointSpec = {
  continuous?: number;
  surge?: number;
  termCap: number;
  /** "leaf" — the component's own amps; "passthrough" — an aggregate walked
   *  through a junction/busbar. */
  source?: "leaf" | "passthrough";
  /** What the endpoint is, whether or not a value resolved. */
  kind: "leaf" | "passthrough" | "missing";
  /** A passthrough that resolved nothing but leads on to a charger's input —
   *  unconstrained by design, because the far side of the cable supplies the
   *  number — rather than to a dead end. */
  open?: boolean;
};

/** On a part with terminals on both rails (a double-pole PV isolator), a
 *  walk entering on one rail stays on it. Junction and single-rail parts pass
 *  everything. */
function onSameRail(def: ComponentDefinition | undefined, entryTermId: string, termId: string): boolean {
  if (!def) return true;
  const entry = def.terminals.find((t) => t.id === entryTermId);
  const other = def.terminals.find((t) => t.id === termId);
  if (!entry || !other) return true;
  const a = terminalPolarity(entry);
  const b = terminalPolarity(other);
  if (a === "other" || b === "other") return true;
  return a === b;
}

/** A shunt is a series resistor — its two power terminals are one electrical
 *  node, so loads past it are not open. */
export function isShuntPassthrough(def: ComponentDefinition): boolean {
  if (def.category !== "monitor") return false;
  const currentCarrying = def.terminals.filter(
    (t) => t.polarity === "positive" || t.polarity === "negative",
  );
  if (currentCarrying.length < 2) return false;
  const hasInput = currentCarrying.some((t) => t.direction === "input");
  const hasOutput = currentCarrying.some((t) => t.direction === "output");
  return hasInput && hasOutput;
}

/** Terminals electrically common (a busbar): a cable landing on one aggregates
 *  the bar's other branches instead of reading the part as a source or sink.
 *  Shared by sizing, voltage, bank and flow-direction walks, so one rule decides
 *  what is "through" everywhere. A shunt counts: it is in series on the negative
 *  bus, and without it every return would tie at the load rank on both ends and
 *  lose its flow stripes. */
export function isPassthroughComponent(def: ComponentDefinition): boolean {
  if (isFuseBlockLike(def)) return false;
  if (def.renderVariant === "busbar") return true;
  if (def.renderVariant === "switch") return true;
  if (def.renderVariant === "junction") return true;
  if (isShuntPassthrough(def)) return true;
  if (def.category !== "distribution") return false;
  if (def.ampOutput != null || def.ampDraw != null || def.ampSurge != null) {
    return false;
  }
  if (def.terminals.length === 0) return false;
  const firstPolarity = def.terminals[0].polarity;
  return def.terminals.every((t) => t.polarity === firstPolarity);
}

/** A panel's input supplies all its positive slots, just as its negative input
 * collects the branch returns. Individual positive slots remain leaves: a
 * branch must never inherit the demand of its neighbouring fused circuits. */
function isPassthroughTerminal(def: ComponentDefinition, termId: string): boolean {
  if (isPassthroughComponent(def)) return true;
  const term = def.terminals.find((t) => t.id === termId);
  return isFuseBlockLike(def) && term != null && (
    terminalPolarity(term) === "negative" ||
    (terminalPolarity(term) === "positive" && term.direction === "input")
  );
}

/** Source on its own rail — which inverts on the negative rail, where a load's
 *  − terminal sources the return current. The two roles only exist to pair a
 *  bus's branches (a bus walked from a source sums its sinks and skips its
 *  parallel sources), so what matters is that each circuit's two ends land
 *  on opposite roles on *both* rails. A PV string is the case that did not:
 *  the panel's − and the MPPT's PV− were both sinks, so a junction between
 *  them skipped everything and the PV return read 0 A / 1.5 mm² beside a
 *  10 mm² PV+ (allegro-33, mareva, van-orion-xs). A panel is a source on
 *  both rails and an MPPT's PV input a sink on both. */
function isSourceLikeTerminal(def: ComponentDefinition, term: Terminal): boolean {
  const polarity = terminalPolarity(term);
  const cat = def.category;
  if (polarity === "positive") {
    if (cat === "battery" || cat === "solar" || cat === "source" || cat === "engine") return true;
    if (cat === "charger" && term.direction === "output") return true;
    if (cat === "distribution" && term.direction === "output") return true;
    return false;
  }
  if (polarity === "negative") {
    if (cat === "load" || cat === "solar") return true;
    if (cat === "distribution" && term.direction === "output") return true;
    return false;
  }
  return false;
}

function isSinkLikeTerminal(def: ComponentDefinition, term: Terminal): boolean {
  const polarity = terminalPolarity(term);
  const cat = def.category;
  if (polarity === "positive") {
    if (cat === "load") return true;
    if (cat === "charger" && term.direction === "input") return true;
    if (cat === "distribution" && term.direction === "input") return true;
    return false;
  }
  if (polarity === "negative") {
    if (cat === "battery" || cat === "source" || cat === "engine") return true;
    if (cat === "charger" && term.direction === "output") return true;
    if (cat === "charger" && term.direction === "input") return true;
    if (cat === "distribution" && term.direction === "input") return true;
    return false;
  }
  return false;
}

type EndpointRole = "source" | "sink" | "unknown";

function isSenseTerminalId(def: ComponentDefinition, termId: string): boolean {
  const term = def.terminals.find((t) => t.id === termId);
  return term != null && isSenseTerminal(def, term);
}

/** Cables per node, keyed by the `cables` array ref — the store replaces the
 *  array on every mutation, so stale entries GC. A self-loop is stored once. */
const cablesByNodeCache = new WeakMap<Cable[], Map<string, Cable[]>>();
const EMPTY_CABLE_LIST: readonly Cable[] = [];

function getCablesAtNode(cables: Cable[], nodeId: string): readonly Cable[] {
  let m = cablesByNodeCache.get(cables);
  if (!m) {
    m = new Map<string, Cable[]>();
    for (const c of cables) {
      let from = m.get(c.fromNodeId);
      if (!from) {
        from = [];
        m.set(c.fromNodeId, from);
      }
      from.push(c);
      if (c.toNodeId !== c.fromNodeId) {
        let to = m.get(c.toNodeId);
        if (!to) {
          to = [];
          m.set(c.toNodeId, to);
        }
        to.push(c);
      }
    }
    cablesByNodeCache.set(cables, m);
  }
  return m.get(nodeId) ?? EMPTY_CABLE_LIST;
}

/** A converter's input remains a sink while its output is generating. State
 * roles describe a bidirectional leaf (for example a starter/alternator), not
 * a reversal of every physical port on a component with separate inputs. */
function leafTerminalRole(
  def: ComponentDefinition,
  term: Terminal,
  nodeId: string,
  simulate: SimulateContext | undefined,
): EndpointRole {
  if (simulate) {
    const option = getEffectiveStateOption(def, nodeId, simulate);
    if (option?.ampOverride === 0) return "unknown";
    if (def.category === "charger" && !isInverterCharger(def) && term.direction === "input") return "sink";
    if (option?.role === "output") return "source";
    if (option?.role === "draw") return "sink";
    // Batteries may absorb charge or deliver current on the same terminal.
    if (def.category === "battery") return "unknown";
  }
  if (isSourceLikeTerminal(def, term)) return "source";
  if (isSinkLikeTerminal(def, term)) return "sink";
  return "unknown";
}

/** "source" — current leaves the terminal into the cable; "sink" — it enters.
 *  Walks through chains of junctions and passthrough busbars. */
function nodeTermRole(
  nodeId: string,
  termId: string,
  originCableId: string,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
  visited: Set<string>,
  simulate: SimulateContext | undefined,
): EndpointRole {
  const node = nodesById[nodeId];
  if (!node) return "unknown";

  if (node.kind !== "component") return "unknown";
  const def = defLookup(node.definitionId, node.customDefinition);
  // A junction is a passthrough even when the catalog lookup fails.
  const isJ = isJunction(node, def);
  const term = !isJ ? def?.terminals.find((t) => t.id === termId) : undefined;
  if (!isJ && (!def || !term)) return "unknown";
  // A sense lead neither sources nor sinks; the shunt behind it must not
  // lend its loads a role through the B+ wire.
  if (def && term && isSenseTerminal(def, term)) return "unknown";
  if (!isJ && def && !isPassthroughTerminal(def, termId)) {
    return term ? leafTerminalRole(def, term, node.id, simulate) : "unknown";
  }
  if (def && simulate) {
    const opt = getEffectiveStateOption(def, node.id, simulate);
    if (opt?.ampOverride === 0) return "unknown";
  }

  if (visited.has(node.id)) return "unknown";
  visited.add(node.id);

  // Selector switch: a cable on a floating terminal doesn't see the rest of the bus.
  const intraConnected = def
    ? getActiveIntraLinkTargets(def, node.id, simulate, termId)
    : undefined;

  let foundSource = false;
  let foundSink = false;
  for (const c of getCablesAtNode(cables, node.id)) {
    if (c.id === originCableId) continue;
    let nextNodeId: string | null = null;
    let nextTermId: string | null = null;
    let localTermId: string | null = null;
    if (c.fromNodeId === node.id) {
      localTermId = c.fromTerminalId;
      nextNodeId = c.toNodeId;
      nextTermId = c.toTerminalId;
    } else if (c.toNodeId === node.id) {
      localTermId = c.toTerminalId;
      nextNodeId = c.fromNodeId;
      nextTermId = c.fromTerminalId;
    }
    if (!nextNodeId || !nextTermId) continue;
    if (intraConnected && !intraConnected.has(localTermId!)) continue;
    if (def && isSenseTerminalId(def, localTermId!)) continue;
    if (!onSameRail(def, termId, localTermId!)) continue;
    const r = nodeTermRole(
      nextNodeId,
      nextTermId,
      c.id,
      nodesById,
      cables,
      defLookup,
      visited,
      simulate,
    );
    if (r === "source") foundSource = true;
    if (r === "sink") foundSink = true;
    if (foundSource && foundSink) break;
  }
  if (foundSource && !foundSink) return "source";
  if (foundSink && !foundSource) return "sink";
  return "unknown";
}

/** Role of the cable's *far* end. Only a leaf is classified; a passthrough
 *  chain reports "unknown" so the skip-same-role rule can't fire on it. */
function farRoleForCable(
  passthroughNodeId: string,
  originCableId: string,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
  simulate: SimulateContext | undefined,
): EndpointRole {
  const origin = cables.find((c) => c.id === originCableId);
  if (!origin) return "unknown";
  let farNodeId: string;
  let farTermId: string;
  if (origin.fromNodeId === passthroughNodeId) {
    farNodeId = origin.toNodeId;
    farTermId = origin.toTerminalId;
  } else if (origin.toNodeId === passthroughNodeId) {
    farNodeId = origin.fromNodeId;
    farTermId = origin.fromTerminalId;
  } else {
    return "unknown";
  }
  const farNode = nodesById[farNodeId];
  if (farNode?.kind !== "component") return "unknown";
  const farDef = defLookup(farNode.definitionId, farNode.customDefinition);
  if (!farDef || isPassthroughTerminal(farDef, farTermId)) return "unknown";
  const farTerm = farDef.terminals.find((t) => t.id === farTermId);
  if (!farTerm) return "unknown";
  return leafTerminalRole(farDef, farTerm, farNode.id, simulate);
}

/** Resolve one endpoint. A junction/busbar sums the branches reachable through
 *  it (Kirchhoff); a component reads its spec through the terminal direction. */
function resolveEndpoint(
  nodeId: string,
  termId: string,
  originCableId: string,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
  visited: Set<string>,
  simulate: SimulateContext | undefined,
): EndpointSpec {
  const node = nodesById[nodeId];
  if (!node) return { termCap: Infinity, kind: "missing" };

  const simulateOption = (
    leafDef: ComponentDefinition | undefined,
  ): SimulateStateOption | undefined => {
    if (!simulate || !leafDef) return undefined;
    return getEffectiveStateOption(leafDef, node.id, simulate);
  };

  // Capture termCap upfront so it clamps the result on every return path.
  let isPassthrough = false;
  let termCap = Infinity;
  let leafTerm: Terminal | undefined;
  let leafDef: ComponentDefinition | undefined;

  if (node.kind === "component") {
    leafDef = defLookup(node.definitionId, node.customDefinition);
    leafTerm = leafDef?.terminals.find((t) => t.id === termId);
    // Unknown connector capacity must not erase a known device demand. The
    // page displays an unpublished (zero) rating as unknown, not as 0 A.
    termCap = leafTerm && leafTerm.maxAmp > 0 ? leafTerm.maxAmp : Infinity;
    // A sense wire carries milliamps whatever hangs off it. Read as a
    // passthrough (a shunt is one), a BMV's B+ lead summed the shunt's whole
    // return, and two batteries on one B+ post read as an 8 A interconnect.
    if (leafDef && leafTerm && isSenseTerminal(leafDef, leafTerm)) {
      return { termCap, continuous: 0, surge: 0, source: "leaf", kind: "leaf" };
    }
    if (isJunction(node, leafDef) || (leafDef && isPassthroughTerminal(leafDef, termId))) {
      isPassthrough = true;
    }
  } else {
    return { termCap, kind: "missing" };
  }

  if (!isPassthrough) {
    if (!leafDef || !leafTerm) return { termCap, kind: "leaf" };
    // Independent ports do not gate one another. A hybrid may produce from
    // PV while its vehicle input is disconnected; shore AC and optional PE
    // must not make a valid DC output read zero. Dispatch still determines
    // whether a supplied port is actually producing current.
    if (simulate?.energizedTerminals) {
      const groups = [
        ...pvInputTerminalGroups(leafDef),
        ...(["input", "output", "bidi"] as const).flatMap(side => dcPortTerminalGroups(leafDef!, side)),
      ];
      const relevant = groups.filter(group => group.some(t => t.id === termId));
      const supplied = relevant.some(group =>
        group.some(t => t.polarity === "positive") &&
        group.some(t => t.polarity === "negative" || t.polarity === "ground") &&
        group.every(t => simulate.energizedTerminals!.has(`${node.id}:${t.id}`)),
      );
      if (relevant.length > 0 && !supplied) {
        return { termCap, continuous: 0, surge: 0, source: "leaf", kind: "leaf" };
      }
    }
    // A charger's connector rating is not an electronic current limit.
    // Preserve declared output current even when its own lug is under-rated.
    if (leafDef.category === "charger" && leafDef.ampOutput != null &&
        dcChargingPortGroups(leafDef).some(port => port.some(t => t.id === termId))) termCap = Infinity;
    const opt = simulateOption(leafDef);
    const loadAmps = componentLoadCurrents(leafDef, node.id, nodesById, cables, defLookup, simulate);
    // Surge rides the draw rail, so a rail-specific state gates it with `ampDraw`.
    const surge = applyStateToAmp(loadAmps.surge, opt, "draw");
    const leafSource = "leaf" as const;
    if (isInverterCharger(leafDef)) {
      const draw = applyStateToAmp(loadAmps.draw, opt, "draw");
      const stage = effectiveChargeCurrent(node, leafDef, simulate);
      const output = stage == null ? undefined : outputTerminalCurrent(leafDef, leafTerm.id, stage);
      return { continuous: draw ?? output, surge, termCap, source: leafSource, kind: "leaf" };
    }
    switch (leafTerm.direction) {
      case "output": {
        const isEngine = leafDef.category === "engine";
        const stage = effectiveChargeCurrent(node, leafDef, simulate);
        const out = stage == null ? undefined : outputTerminalCurrent(leafDef, leafTerm.id, stage);
        return {
          // A definitive 0, not `undefined`: an unconstrained leaf lets the far
          // end push its aggregate back through the idle Alt B+.
          continuous: isEngine ? out ?? 0 : out,
          // An engine's `ampSurge` is cranking inrush on the starter rail, not
          // on Alt B+, which would size that run for the 400 A crank.
          surge: isEngine ? undefined : surge,
          termCap,
          source: leafSource,
          kind: "leaf",
        };
      }
      case "input": {
        // On a charger/MPPT/DC-DC, `ampOutput` describes the BATT side, so the
        // input is left unconstrained. An inverter's AC output is another rail.
        const hasDcOutput =
          leafDef.category === "charger" &&
          leafDef.terminals.some((t) => t.direction === "output");
        if (hasDcOutput) {
          const output = effectiveChargeCurrent(node, leafDef, simulate) ?? 0;
          const input = isPvTerminalId(leafTerm.id) ? undefined : dcDcInputCurrent(leafDef, node.id, nodesById, cables, defLookup, output, simulate);
          if (input != null) return {
            continuous: applyStateToAmp(leafDef.ampDraw, opt, "draw") ?? input,
            termCap, source: "leaf", kind: "leaf",
          };
          return { termCap, kind: "leaf", open: true };
        }
        // Read both rails: a combined engine stud carries starter draw and alt output.
        const draw = applyStateToAmp(loadAmps.draw, opt, "draw");
        const output = effectiveChargeCurrent(node, leafDef, simulate);
        // With a dedicated Alt B+ output the starter input is cranking-only and
        // must not fall back to `ampOutput` — that double-counts the alternator.
        const engineHasAltOutput =
          leafDef.category === "engine" &&
          leafDef.terminals.some((t) => t.direction === "output");
        // A definitive 0, not `undefined` — an unconstrained leaf lets the
        // passthrough side dominate and the idle starter appears to source it.
        const continuous = engineHasAltOutput ? draw ?? 0 : draw ?? output;
        return { continuous, surge, termCap, source: leafSource, kind: "leaf" };
      }
      case "bidi": {
        if (leafTerm.polarity === "signal") return { termCap, kind: "leaf" };
        const draw = applyStateToAmp(loadAmps.draw, opt, "draw");
        const output = effectiveChargeCurrent(node, leafDef, simulate);
        return { continuous: draw ?? output, surge, termCap, source: leafSource, kind: "leaf" };
      }
      default:
        return { termCap, kind: "leaf" };
    }
  }

  // An open switch breaks the path: report a 0-A leaf so both its cables read
  // 0 A whatever sits on the far side.
  if (leafDef && simulate) {
    const opt = simulateOption(leafDef);
    if (opt?.ampOverride === 0) {
      return { termCap, continuous: 0, surge: 0, source: "leaf", kind: "leaf" };
    }
  }

  // Aggregate the parallel branches on the far side of the passthrough: a cable
  // whose far end sources is feeding the bus, so sum its sinks and skip the
  // parallel feeders; mirror for a sink. An "unknown" far end sums everything.
  if (visited.has(node.id)) return { termCap, kind: "passthrough" };
  visited.add(node.id);

  const farRole = farRoleForCable(node.id, originCableId, nodesById, cables, defLookup, simulate);

  const intraConnected = leafDef
    ? getActiveIntraLinkTargets(leafDef, node.id, simulate, termId)
    : undefined;

  let continuous = 0;
  let continuousSet = false;
  let surge = 0;
  let surgeSet = false;
  let open = false;

  for (const c of getCablesAtNode(cables, node.id)) {
    if (c.id === originCableId) continue;
    let nextNodeId: string | null = null;
    let nextTermId: string | null = null;
    let localTermId: string | null = null;
    if (c.fromNodeId === node.id) {
      localTermId = c.fromTerminalId;
      nextNodeId = c.toNodeId;
      nextTermId = c.toTerminalId;
    } else if (c.toNodeId === node.id) {
      localTermId = c.toTerminalId;
      nextNodeId = c.fromNodeId;
      nextTermId = c.fromTerminalId;
    }
    if (!nextNodeId || !nextTermId) continue;
    if (intraConnected && !intraConnected.has(localTermId!)) continue;
    // Nothing is aggregated *through* a sense post on this passthrough.
    if (leafDef && isSenseTerminalId(leafDef, localTermId!)) continue;
    // A double-pole isolator is two conductors in one box: the PV+ pole must
    // not sum the panels' − leads on top of their + leads.
    if (!onSameRail(leafDef, termId, localTermId!)) continue;

    if (farRole !== "unknown") {
      const branchRole = nodeTermRole(
        nextNodeId,
        nextTermId,
        c.id,
        nodesById,
        cables,
        defLookup,
        new Set([node.id]),
        simulate,
      );
      if (farRole === "source" && branchRole === "source") continue;
      if (farRole === "sink" && branchRole === "sink") continue;
    }

    const sub = resolveEndpoint(
      nextNodeId,
      nextTermId,
      c.id,
      nodesById,
      cables,
      defLookup,
      visited,
      simulate,
    );
    if (sub.continuous != null) {
      continuous += sub.continuous;
      continuousSet = true;
    }
    if (sub.surge != null) {
      surge += sub.surge;
      surgeSet = true;
    }
    if (sub.open) open = true;
  }

  return {
    continuous: continuousSet ? continuous : undefined,
    surge: surgeSet ? surge : undefined,
    termCap,
    source: continuousSet || surgeSet ? "passthrough" : undefined,
    kind: "passthrough",
    open,
  };
}

function pickPeakValue(s: EndpointSpec): number | undefined {
  if (s.surge != null && s.continuous != null) return Math.max(s.surge, s.continuous);
  return s.surge ?? s.continuous;
}

/** Continuous per side. Ampacity is a continuous rating, so surge is only a
 *  fallback for a component that declares no continuous draw. */
function pickContinuousValue(s: EndpointSpec): number | undefined {
  return s.continuous ?? s.surge;
}

type CableCurrents = { continuous: number; peak: number };

/** Complete PV arrays use conserved source current. Other cables retain the
 * legacy endpoint estimate until the system boundary supplies a DC envelope. */
function resolveCableCurrents(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
  simulate: SimulateContext | undefined,
  /** Let the cable's own fuse or inline holder stand in when neither side of
   *  the walk resolves. Off when a path-walked `demand` is available — that is
   *  the real load, and a fused feed between two distribution parts has no leaf
   *  on either end to read. */
  fuseFallback = true,
): CableCurrents {
  if (!simulate) {
    const pv = computePvCurrentAnalysis(nodesById, cables, defLookup);
    const amps = pv.byCable.get(cable.id);
    if (amps != null) return { continuous: amps, peak: amps };
    if (pv.unresolvedCableIds.has(cable.id)) return { continuous: 0, peak: 0 };
  }
  const fromNode = nodesById[cable.fromNodeId];
  const toNode = nodesById[cable.toNodeId];
  if (!fromNode || !toNode) return { continuous: 5, peak: 5 };

  const sideA = resolveEndpoint(
    cable.fromNodeId,
    cable.fromTerminalId,
    cable.id,
    nodesById,
    cables,
    defLookup,
    new Set(),
    simulate,
  );
  const sideB = resolveEndpoint(
    cable.toNodeId,
    cable.toTerminalId,
    cable.id,
    nodesById,
    cables,
    defLookup,
    new Set(),
    simulate,
  );

  const termCap = Math.min(sideA.termCap, sideB.termCap);

  // A fuse bounds what the conductor may carry; it is not the current flowing.
  // Before 2026-08-28 a fused cable read its fuse as continuous current, which
  // put a 40 A ANL feed at "40 A" for 14 A of loads, ran V-drop at fuse ratings
  // and — with the 1.25 × headroom on top — landed every fused branch a table
  // step fat. The rating is now an ampacity floor in `computeCableSpec`; here
  // it only stands in when neither side of the walk resolves at all.
  // A battery-post MRBF is never a fallback: it is sized for the bank, and
  // reading it as this cable's current put every lead off a 300 A post at 4/0.
  // A rating of 0 is "no fuse" (the AC sentinel, and what `recommendFuse`
  // answers for a run with no current) and never a current either. A stored
  // 1 A *is* still read: the connect flow used to write the ladder's first rung
  // onto runs that resolved nothing, but the data cannot tell that 1 A from one
  // chosen for a sense lead, and the intent above — an unknown load behind a
  // known fuse still has to carry it — holds for both. It sizes the minimum
  // conductor either way, and `fuse-trips-under-load` names it the moment a
  // real load lands behind it.
  const fallbackFuse = (() => {
    if (simulate || !fuseFallback) return undefined;
    const eff = getEffectiveCableFuse(cable, nodesById, defLookup);
    const rating = eff.source.rating === "cable" ? eff.ratingA : eff.holderRatingA;
    return rating != null && rating > 0 ? rating : undefined;
  })();

  // A leaf beats a passthrough aggregate, so a small parallel source on a bus
  // can't clamp a load cable below its draw. A passthrough side that resolved
  // to nothing is a dead end: the cable carries 0, not the bus's whole sum —
  // unless it leads on to a charger's input, which is unconstrained by design
  // (a PV combiner between two panels and an MPPT read 0 A on the trunk).
  const pickFromSides = (
    aVal: number | undefined,
    aSrc: EndpointSpec["source"],
    aKind: EndpointSpec["kind"],
    aOpen: boolean,
    bVal: number | undefined,
    bSrc: EndpointSpec["source"],
    bKind: EndpointSpec["kind"],
    bOpen: boolean,
    fallback: number,
  ): number => {
    if (aVal != null && bVal != null) {
      if (aSrc === "leaf" && bSrc === "passthrough") return Math.min(termCap, aVal);
      if (bSrc === "leaf" && aSrc === "passthrough") return Math.min(termCap, bVal);
      return Math.min(termCap, Math.min(aVal, bVal));
    }
    if (aVal != null) {
      if (aSrc === "passthrough" && bKind === "passthrough" && !bOpen) {
        return Math.min(termCap, fallback);
      }
      return Math.min(termCap, aVal);
    }
    if (bVal != null) {
      if (bSrc === "passthrough" && aKind === "passthrough" && !aOpen) {
        return Math.min(termCap, fallback);
      }
      return Math.min(termCap, bVal);
    }
    return Math.min(termCap, fallback);
  };

  // No fabricated default when neither side resolves — a default disagrees
  // with the diagnostic's path walk and fires false "wire undersized" warnings;
  // real demand arrives via the `demand` parameter. A fuse on the cable is the
  // one exception: an unknown load behind a known fuse still has to carry it.
  const unresolved = fallbackFuse != null ? Math.min(termCap, fallbackFuse) : 0;
  const peak = pickFromSides(
    pickPeakValue(sideA),
    sideA.source,
    sideA.kind,
    sideA.open ?? false,
    pickPeakValue(sideB),
    sideB.source,
    sideB.kind,
    sideB.open ?? false,
    unresolved,
  );
  const continuous = pickFromSides(
    pickContinuousValue(sideA),
    sideA.source,
    sideA.kind,
    sideA.open ?? false,
    pickContinuousValue(sideB),
    sideB.source,
    sideB.kind,
    sideB.open ?? false,
    peak,
  );
  return { continuous, peak };
}

/** Peak only; `computeCableSpec` resolves continuous and peak separately. */
export function estimateCableCurrent(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
  simulate?: SimulateContext,
): number {
  return resolveCableCurrents(cable, nodesById, cables, defLookup, simulate).peak;
}

export type CableSpec = {
  /** Peak — max of continuous and surge. Drives overload checks. */
  current: number;
  /** Continuous — drives wire sizing and V-drop. */
  continuous: number;
  /** Design ampacity including headroom only on sustained current. Separate
   * from isCranking, which also governs the overcurrent-protection exemption. */
  requiredAmpacity?: number;
  /** Provisional values need a topology/current review before relying on sizing. */
  currentBasis?: "complete-route-envelope" | "provisional-fallback";
  sourceAllocationDependent?: boolean;
  sustainedCurrent?: number;
  startingCurrent?: number;
  wire: WireSpec;
  fuse: number;
  /** Starter role is retained, but missing/zero starting data cannot rate an optional fuse. */
  crankingFuseUnverified?: boolean;
  /** Derived advice only; any existing installed declaration stays visible. */
  fuseRecommendationUnavailableReason?: "selected-family-range" | "manufacturer-wire-ampacity" | "manufacturer-installation" | "equipment-slot-limit" | "terminal-limit";
  /** Maximum local inline fuse allowed by either directly connected series contact. */
  terminalFuseLimitA?: number;
  /** The effective rating belongs solely to the battery post, not a branch fuse. */
  protectionIsBatteryPost?: true;
  /** The current can be known while the manufacturer wire chart still needs verification. */
  manufacturerInstallationUnverified?: true;
  /** Separate equipment fuse advice; an inherited battery fuse remains its own declaration. */
  manufacturerFuseA?: number;
  /** This conductor's loss when its return is drawn; a mirrored-loop
   * estimate otherwise. Complete-loop diagnostics sum physical I*R losses. */
  vDrop: number;
  vDropPct: number;
  /** True on the engine-cranking circuit. Don't re-derive it from a
   *  peak/continuous ratio — that also matches every inverter. */
  isCranking: boolean;
  /** Nominal circuit voltage — 12 V on the bus, sum of Vmp on a series PV string. */
  nominalV: number;
  /** ABYC E-11.14.2.6 allowance (%) this cable was sized against — 3 % critical,
   *  10 % general lighting. */
  vdropAllowancePct: number;
  /** An AC conductor (shore inlet, generator, charger AC-in, inverter AC-out).
   *  Sized by the inlet rating alone: `fuse` is 0 — the inlet's breaker is its
   *  protection (ABYC E-11.10.2) — and `vDrop` is not computed. `current` is
   *  the rating it was sized for, not a measured DC current. */
  isAc: boolean;
  /** Profile/installation coverage is separate from physical current provenance. */
  sizingVerificationRequired?: true;
  sizingVerificationReasons?: string[];
  designProfileId?: string;
};

/** Default allowance — the 3 % side of ABYC E-11.14.2.6. */
export const VDROP_WARN_PCT = VDROP_PCT_CRITICAL;

export function hasHighVDrop(spec: CableSpec): boolean {
  return spec.vDropPct > spec.vdropAllowancePct;
}

/** ~5 % slack so a cable sized exactly at the boundary doesn't trip on float noise. */
export const OVERLOAD_TOLERANCE = 1.05;

/** Continuous vs. wire ampacity. Ampacity is a continuous rating, so a
 *  300 A-surge starter feed sized per ABYC E-11 is not undersized. */
export function isOverloaded(spec: CableSpec): boolean {
  return spec.continuous > spec.wire.ampacity * OVERLOAD_TOLERANCE;
}

function profileWireByMm2(mm2: number, policy: ElectricalDesignPolicy): WireSpec {
  // Only legacy marine uses the extra calculator AWG pool. A profile cannot
  // inherit an unreviewed ampacity merely because a manual area is familiar.
  return wireByMm2(mm2, policy.wireTable, policy.coverage.enforceTableCeiling ? [] : undefined);
}

/** A complete signed route envelope replaces the local unsigned estimate.
 * Incomplete models retain both bounds and identify that fallback explicitly. */
export type CableDemandHint = {
  continuous: number;
  peak: number;
  requiredAmpacity?: number;
  startingCurrent?: number;
  sustainedCurrent?: number;
  currentBasis?: "complete-route-envelope" | "provisional-fallback";
  sourceAllocationDependent?: boolean;
};

export function computeCableSpec(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup = getDefinition,
  simulate?: SimulateContext,
  demand?: CableDemandHint,
  /** Minimum wire size (mm²) from `path-vdrop-budget.ts`, a floor under the
   *  per-cable pick: every cable can clear 3 % while the whole run does not. */
  pathMinWireSizeMm2?: number,
  /** On the engine-cranking circuit, which ABYC E-11.10 exempts from inline
   *  overcurrent protection and E-11 from the 1.25× continuous-load derating. */
  isCrankingCircuit = false,
  /** This circuit's ABYC E-11.14.2.6 allowance (%). Defaults to the 3 % side. */
  vdropAllowancePct: number = VDROP_PCT_CRITICAL,
  /** Current inside a parallel battery bank, from `battery-bank.ts`. *Replaces*
   *  the per-endpoint walk (a battery declares no amps, so that walk reads 0 A),
   *  but never replaces `demand`. */
  bankCurrentA?: number,
  /** Static declared starting-current evidence for every starter sharing this conductor. */
  hasDeclaredStartingCurrent = false,
  manufacturerPolicy?: FridgeCablePolicy,
  breakerAmpacityFloor = 0,
  designProfile: DesignProfile = resolveDesignProfile({}),
): CableSpec {
  const policy = designProfile.policy;
  const coverage = policy.coverage;
  // Calculations remain available as provisional planning values outside the
  // selected policy. Their provenance must travel with every downstream use.
  const finish = (spec: CableSpec, capacityFloor = 0, minimumArea = 0): CableSpec => {
    const reasons: string[] = [];
    if (designProfile.status !== "available") reasons.push("unknown-profile");
    if (!coverage.ac && spec.isAc) reasons.push("ac-outside-profile");
    if (!spec.isAc) {
      if (!coverage.signals && isSenseCable(cable, nodesById, defLookup)) reasons.push("signal-outside-profile");
      if (!coverage.cranking && (spec.isCranking || (demand?.startingCurrent ?? 0) > 0)) reasons.push("starting-outside-profile");
      if (!coverage.pv) {
        const pv = computePvCurrentAnalysis(nodesById, cables, defLookup);
        if (pv.byCable.has(cable.id) || pv.unresolvedCableIds.has(cable.id)) reasons.push("pv-outside-profile");
      }
      if (coverage.enforceTableCeiling && (spec.nominalV <= coverage.minDcVoltage || spec.nominalV > coverage.maxDcVoltage)) reasons.push("voltage-outside-profile");
      if (coverage.minCurrentA > 0) {
        // LV2020's threshold concerns connected load, not instantaneous draw.
        // Keep a switched-off appliance eligible in simulation.
        const connected = resolveCableCurrents(cable, nodesById, cables, defLookup, undefined, false);
        if (Math.max(connected.continuous, demand?.continuous ?? 0) < coverage.minCurrentA) reasons.push("connected-load-outside-profile");
      }
    }
    if (!coverage.engineBay && [cable.fromNodeId, cable.toNodeId].some(id => {
      let node: SystemNode | undefined = nodesById[id];
      const seen = new Set<string>();
      while (node && !seen.has(node.id)) {
        if (node.kind === "zone" && node.isEngineBay) return true;
        seen.add(node.id);
        node = node.parentId ? nodesById[node.parentId] : undefined;
      }
      return false;
    })) reasons.push("engine-route-unverified");
    if (coverage.enforceTableCeiling) {
      if (!policy.wireTable.some(w => Math.abs(w.mm2 - spec.wire.mm2) < 0.00001)) reasons.push("wire-outside-profile");
      const ceiling = policy.wireTable[policy.wireTable.length - 1];
      if (capacityFloor > ceiling.ampacity * cableAmbientDerating(cable, nodesById, policy.engineBayDerating)
          || minimumArea > ceiling.mm2) reasons.push("table-capacity-exceeded");
      if (!spec.isAc && spec.wire.mm2 >= ceiling.mm2 && spec.vDropPct > spec.vdropAllowancePct + 1e-9) reasons.push("table-voltage-drop-exceeded");
    }
    return reasons.length ? { ...spec, designProfileId: designProfile.id, sizingVerificationRequired: true, sizingVerificationReasons: reasons }
      : spec;
  };
  // An AC run is outside the DC model: no path-walked demand, no 12 V V-drop,
  // and never a fuse. Its conductor has to carry what the inlet's breaker
  // allows through — a 30 A inlet means a 30 A conductor on L, N and PE alike
  // — with the E-11.4.1.1 floor. Sized at 12 V it drew a 1 A fuse on the
  // neutral and reported "30 A of DC" on a protective earth.
  const ac = acCableInfo(cable, nodesById, defLookup, cables, simulate);
  if (ac) {
    const wire =
      cable.wireSizeMm2Override != null
        ? profileWireByMm2(cable.wireSizeMm2Override, policy)
        : selectWire(0, 0, ac.nominalV, vdropAllowancePct, 1, ac.ratingA,
          cableAmbientDerating(cable, nodesById, policy.engineBayDerating),
          // AC returns before DC sizing. Retain exact-model installation
          // floors here too, rounded upward through the metric wire table.
          Math.max(policy.minimumWireMm2, installationWireMinimum(cable.id, nodesById, cables, defLookup)), policy.wireTable);
    return finish({
      current: ac.ratingA,
      continuous: ac.ratingA,
      wire,
      fuse: 0,
      vDrop: 0,
      vDropPct: 0,
      nominalV: ac.nominalV,
      isCranking: false,
      vdropAllowancePct,
      isAc: true,
    }, ac.ratingA, installationWireMinimum(cable.id, nodesById, cables, defLookup));
  }
  // A direct caller sees only the PV contribution. The system boundary
  // checks for a shared DC return before certifying the complete envelope.
  if (!simulate && demand == null) {
    const pv = computePvCurrentAnalysis(nodesById, cables, defLookup);
    const amps = pv.byCable.get(cable.id);
    if (amps != null) demand = { continuous: amps, peak: amps,
      requiredAmpacity: amps * policy.continuousLoadHeadroom,
      currentBasis: "provisional-fallback" };
    else if (pv.unresolvedCableIds.has(cable.id)) demand = {
      continuous: 0, peak: 0, currentBasis: "provisional-fallback",
    };
  }
  const local = bankCurrentA != null
    ? { continuous: bankCurrentA, peak: bankCurrentA }
    : resolveCableCurrents(
        cable,
        nodesById,
        cables,
        defLookup,
        simulate,
        demand == null,
      );
  // Complete paired routes already account for source alternatives and
  // opposing transfers. A local unsigned walk must not restore their sum.
  // Incomplete models retain the old bank/local bounds as a fallback.
  const completeEnvelope = !simulate && demand?.currentBasis === "complete-route-envelope";
  const liveCurrent = simulate ? computeLiveCableCurrents(nodesById, cables, defLookup, simulate).get(cable.id) : undefined;
  const continuous = liveCurrent != null ? Math.abs(liveCurrent) : demand != null
    ? completeEnvelope ? demand.continuous : Math.max(local.continuous, demand.continuous)
    : local.continuous;
  const peak = liveCurrent != null ? Math.abs(liveCurrent) : demand != null
    ? completeEnvelope ? demand.peak : Math.max(local.peak, demand.peak)
    : local.peak;
  // Cranking uses the planner's conservative starting voltage-drop target,
  // and the fuse comes from `recommendCrankingFuse`, not the NEC
  // 1.25 × peak ladder, which lands below the continuous draw here.
  const isCranking = isCrankingCircuit;
  // Starting duty and OCPD exemption are independent: a parallel battery tie
  // can carry starter current while still requiring overcurrent protection.
  const hasStartingDuty = isCranking || (!simulate && (demand?.startingCurrent ?? 0) > 0);
  const headroom = isCranking ? 1 : policy.continuousLoadHeadroom;
  const requiredAmpacity = demand?.requiredAmpacity != null && !simulate
    ? demand.requiredAmpacity + Math.max(0, continuous - demand.continuous) * policy.continuousLoadHeadroom
    : continuous * headroom;
  const maxDropPct = hasStartingDuty ? policy.voltageDrop.startingPct : vdropAllowancePct;
  // ABYC E-11.10.1.2 requires OCPD ≤ conductor ampacity, so the wire must host
  // the fuse this cable will get: the smallest standard rating ≥ 1.25 × the
  // continuous load that still holds the surge (`SURGE_HOLD_FACTOR`). Not
  // 1.25 × peak — that put a 1500 W inverter on a 400 A fuse and 4/0 wire.
  // Cranking is exempt (its fuse rides the class-T curve) and so is a negative
  // or ground leg, which never carries the fuse (E-11.10.1 protects the
  // ungrounded conductor) and was being sized for one anyway.
  const isNegativeLeg =
    resolveCablePolarity(cable, nodesById, cables) === "negative";
  // Exact-model motor protection supersedes the generic surge heuristic.
  // The diagnostic still requires the specified breaker/curve to be verified;
  // its amperage is a sizing floor, not permission to shrink a manual wire.
  const manufacturerBreakerA = installationBreakerRating(cable.id, nodesById, cables, defLookup);
  const manufacturerFuseA = isNegativeLeg ? undefined : manufacturerPolicy?.fuseA;
  const fuseAmpacityFloor =
    isCranking || isNegativeLeg
      ? 0
      : manufacturerBreakerA ?? manufacturerFuseA ?? recommendFuse(continuous, Number.POSITIVE_INFINITY, peak, policy);
  const effectiveFuse = getEffectiveCableFuse(cable, nodesById, defLookup);
  // A user-pinned fuse or an inline holder's rating is *this* conductor's OCPD
  // and therefore a wire floor (E-11.10.1.2). Excluded: cranking and
  // `fuseAmpAuto` (derived FROM the wire — it must not feed back). An inherited
  // battery-post MRBF is a floor only on the bank's *trunk* — post to switch,
  // bus, shunt or holder — because that conductor has no other OCPD. A load or
  // charger wired straight to the post keeps its own size and gets
  // `inline-fuse-needed` instead of 4/0 to host a 300 A MRBF under a 25 A load.
  const postFuseFloor =
    !isCranking &&
    effectiveFuse.source.rating === "battery" &&
    effectiveFuse.holderRatingA == null &&
    effectiveFuse.ratingA != null &&
    isBankTrunk(cable, nodesById, defLookup)
      ? effectiveFuse.ratingA
      : 0;
  const manualFuseFloor = isCranking
    ? 0
    : Math.max(
        cable.fuseAmpAuto !== true &&
          effectiveFuse.source.rating === "cable" &&
          effectiveFuse.ratingA != null
          ? effectiveFuse.ratingA
          : 0,
        effectiveFuse.holderRatingA ?? 0,
        postFuseFloor,
      );
  const nominalV = resolveCableNominalVoltage(cable, nodesById, cables, defLookup, undefined, simulate);
  const dropWeight = cableVoltageDropWeight(cable.id, nodesById, cables, defLookup);
  const sizingLengthM = cable.lengthM * (hasStartingDuty ? 1 : cableSizingDropWeight(cable.id, nodesById, cables, defLookup));
  let wire: WireSpec;
  if (cable.wireSizeMm2Override != null) {
    wire = profileWireByMm2(cable.wireSizeMm2Override, policy);
  } else {
    const ambientDerating = cableAmbientDerating(cable, nodesById, policy.engineBayDerating);
    // Cranking sizes on two currents: V-drop at `peak` (an engine can declare
    // `ampDraw: 1` with the whole crank in `ampSurge`), ampacity at `continuous`
    // with no 1.25× on the brief starter portion. Sustained accessory or
    // charging demand still retains its headroom in requiredAmpacity.
    const auto = selectWire(
      hasStartingDuty ? peak : continuous,
      sizingLengthM,
      nominalV,
      maxDropPct,
      0,
      Math.max(requiredAmpacity, fuseAmpacityFloor, manualFuseFloor, breakerAmpacityFloor),
      ambientDerating,
      policy.minimumWireMm2,
      policy.wireTable,
    );
    const minimumMm2 = Math.max(
      pathMinWireSizeMm2 ?? 0,
      manufacturerPolicy?.minimumWireMm2 ?? 0,
      installationWireMinimum(cable.id, nodesById, cables, defLookup),
    );
    // Automatic area floors must stay in the supported selection table.
    // A manufacturer's rounded AWG area (e.g. 107.2 mm²) is not a manual
    // custom wire. Round upward without reducing the ampacity/V-drop pick;
    // retain the table ceiling when no represented conductor meets the floor.
    wire = policy.wireTable.find(w => w.mm2 >= Math.max(auto.mm2, minimumMm2))
      ?? policy.wireTable[policy.wireTable.length - 1];
  }
  // Selection, protection and overload checks must all use the same effective
  // ampacity, whether the installation factor is explicit or inferred.
  const ambientDerating = cableAmbientDerating(cable, nodesById, policy.engineBayDerating);
  if (ambientDerating !== 1) wire = { ...wire, ampacity: wire.ampacity * ambientDerating };
  const vDrop = voltageDrop(continuous, cable.lengthM, wire.mm2)
    * dropWeight;
  const vDropPct = (vDrop / nominalV) * 100;
  const crankingFuseUnverified = isCranking && !hasDeclaredStartingCurrent;
  const proposedFuse = isCranking
      ? crankingFuseUnverified ? 0 : recommendCrankingFuse(continuous, policy)
      : manufacturerBreakerA ?? manufacturerFuseA ?? recommendFuse(continuous, wire.ampacity, peak, policy);
  const familyExcludesRecommendation = cableFuseFamilyExcludesRating(cable, Object.values(nodesById), defLookup, proposedFuse);
  const contacts = seriesFuseTerminals(cable, nodesById, defLookup, isCranking);
  const terminalFuseLimitA = contacts.length ? Math.min(...contacts.map(({ terminal }) => terminal.maxAmp)) : undefined;
  // Keep duty/protection wire floors above even when this family cannot host
  // the proposed rating. Never increase the fuse to a family minimum or alter
  // a saved declaration to make an unsupported recommendation fit.
  const unavailableReason = familyExcludesRecommendation ? "selected-family-range"
    : manufacturerFuseA != null && manufacturerFuseA > wire.ampacity ? "manufacturer-wire-ampacity"
    : !isNegativeLeg && manufacturerPolicy?.fuseUnverified ? "manufacturer-installation"
    : fuseExceedsTerminalLimit({ terminalFuseLimitA }, proposedFuse) ? "terminal-limit" : undefined;
  const fuse = effectiveFuse.ratingA ?? (unavailableReason ? 0 : proposedFuse);
  return finish({
    current: peak,
    continuous,
    requiredAmpacity,
    ...(!simulate && demand?.currentBasis ? { currentBasis: demand.currentBasis } : {}),
    ...(!simulate && demand?.sourceAllocationDependent ? { sourceAllocationDependent: true } : {}),
    ...(!simulate && demand?.sustainedCurrent != null ? { sustainedCurrent: demand.sustainedCurrent } : {}),
    ...(!simulate && demand?.startingCurrent != null ? { startingCurrent: demand.startingCurrent } : {}),
    wire,
    fuse,
    ...(crankingFuseUnverified ? { crankingFuseUnverified: true } : {}),
    ...(unavailableReason ? { fuseRecommendationUnavailableReason: unavailableReason } : {}),
    ...(terminalFuseLimitA != null ? { terminalFuseLimitA } : {}),
    ...(effectiveFuse.source.rating === "battery" && effectiveFuse.holderRatingA == null ? { protectionIsBatteryPost: true as const } : {}),
    ...(manufacturerPolicy?.manufacturerInstallationUnverified ? { manufacturerInstallationUnverified: true as const } : {}),
    ...(manufacturerFuseA != null ? { manufacturerFuseA } : {}),
    vDrop,
    vDropPct,
    nominalV,
    isCranking,
    vdropAllowancePct,
    isAc: false,
  }, Math.max(requiredAmpacity, fuseAmpacityFloor, manualFuseFloor, breakerAmpacityFloor), Math.max(pathMinWireSizeMm2 ?? 0,
    manufacturerPolicy?.minimumWireMm2 ?? 0, installationWireMinimum(cable.id, nodesById, cables, defLookup)));
}
